<script setup lang="ts">
import { searchStore as $searchStore } from '@layers/common/app/services/search'
import { onClickOutside } from '@vueuse/core'

// Props
const { variant = 'inline', hideInput = false } = defineProps<{
  variant?: 'inline' | 'overlay'
  hideInput?: boolean
}>()

const { t } = useI18n()

// State
const searchTerm = ref('')
const isOpen = ref(false)
const searchContainerRef = ref<HTMLElement>()
const inputRef = ref<HTMLInputElement>()
const { isMobile } = useDevice()
const route = useRoute()

const getRouteBaseName = useRouteBaseName()
const baseRouteName = computed(() => getRouteBaseName(route))
const isSearchPage = computed(() => baseRouteName.value === 'search')
const initialSearchTerm = computed(() => route.query.q?.toString() || '')

// Watch for route changes to close search and clear term
watch(() => route.fullPath, (newPath, oldPath) => {
  // Only close if the route actually changed
  if (oldPath && newPath !== oldPath && isOpen.value) {
    isOpen.value = false
    searchTerm.value = ''
  }
})

// Debounced search
const debouncedSearchTerm = refDebounced(searchTerm, 300)

// Watch for searchTerm changes to auto-open when typing
watch(searchTerm, (newValue, oldValue) => {
  // Don't auto-open on search page when the value matches the initial search term from URL
  if (isSearchPage.value && newValue === initialSearchTerm.value) {
    return
  }

  // Auto-open when user types (value changes from empty or different value)
  if (newValue.length >= 2 && !isOpen.value && oldValue !== undefined) {
    isOpen.value = true
  }
})

// Fetch search results
const { data, status } = useAsyncData(
  `search-${variant}`,
  async () => {
    if (debouncedSearchTerm.value.length < 2) {
      return null
    }
    const result = await $searchStore({
      take: 20,
      skip: 0,
      sort: {
        popularityScore: 'DESC',
      },
      groupByProduct: true,
      term: debouncedSearchTerm.value,
    })

    return result
  },
  {
    watch: [debouncedSearchTerm],
  },
)

// Computed properties
const searchResults = computed(() => data.value?.search.items || [])
const searchCollectionsResults = computed(() => data.value?.searchCollections?.items || [])

// Utility function to format collection breadcrumbs
function _formatCollectionPath(collection: any) {
  if (!collection.breadcrumbs || collection.breadcrumbs.length === 0) {
    return ''
  }

  // Filter out ROOT_COLLECTION and build path
  const path = collection.breadcrumbs
    .filter((crumb: any) => crumb.name !== '__root_collection__')
    .map((crumb: any) => crumb.name)
    .join(' › ')

  return path
}
const hasResults = computed(() => searchResults.value.length > 0 || searchCollectionsResults.value.length > 0)
const isLoading = computed(() => status.value === 'pending' && debouncedSearchTerm.value.length >= 2)
const showEmptyState = computed(() =>
  !isLoading.value
  && debouncedSearchTerm.value.length >= 2
  && !hasResults.value,
)

// Computed property for popover state
const popoverOpen = computed({
  get: () => isOpen.value && searchTerm.value.length >= 2,
  set: (value) => {
    // Don't close if we're on search page and just focused
    if (!value && !isSearchPage.value) {
      handleClose()
    }
  },
})

// Methods
function handleOpen() {
  // On search page, only open if the search term differs from the URL query (for inline variant only)
  if (variant !== 'overlay' && isSearchPage.value && route.query.q && searchTerm.value === route.query.q.toString()) {
    return
  }
  isOpen.value = true
  if (!hideInput) {
    nextTick(() => {
      inputRef.value?.focus?.()
    })
  }
}

function handleClose() {
  isOpen.value = false
}

function handleSearchSubmit() {
  if (searchTerm.value.trim()) {
    handleClose()
    const router = useRouter()
    const localePath = useLocalePath()
    router.push(localePath({ path: '/search', query: { q: searchTerm.value } }))
  }
}

// Click outside handler for desktop (not needed with Popover as it handles its own outside clicks)
// But we'll keep it for the mobile/fallback input
onClickOutside(searchContainerRef, () => {
  if (!isMobile && isOpen.value && variant !== 'overlay') {
    handleClose()
  }
})

// Keyboard shortcuts and initialization
onMounted(() => {
  // Initialize searchTerm from route query if on search page
  if (isSearchPage.value && route.query.q) {
    searchTerm.value = route.query.q.toString()
  }

  // Clear the mount flag after a tick to allow future auto-opens
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen.value) {
      handleClose()
    }
  }
  window.addEventListener('keydown', handleEscape)
  onUnmounted(() => window.removeEventListener('keydown', handleEscape))
})

// Handle product click
function handleProductClick() {
  handleClose()
}

// Expose methods and state for parent components
defineExpose({
  handleOpen,
  searchTerm,
})
</script>

<template>
  <div ref="searchContainerRef" class="relative">
    <!-- Desktop Search with Popover -->
    <UiPopover
      v-if="!isMobile && variant !== 'overlay'"
      v-model:open="popoverOpen"
    >
      <UiPopoverTrigger as-child>
        <div v-if="!hideInput" class="relative">
          <label for="searchbar" class="sr-only">
            {{ t("general.search.label") }}
          </label>
          <UiInput
            ref="inputRef"
            v-model="searchTerm"
            :placeholder="t('general.search_placeholder')"
            class="bg-gray-100 pl10"
            @focus.stop="handleOpen"
            @click.stop="handleOpen"
            @keyup.enter="handleSearchSubmit"
          />
          <span
            class="absolute start-0 inset-y-0 flex items-center justify-center px2"
          >
            <Icon name="lucide:search" />
          </span>
        </div>
      </UiPopoverTrigger>

      <UiPopoverContent
        class="max-h-[600px] w-[800px] overflow-hidden p-0"
        align="center"
      >
        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center py-12">
          <div class="i-lucide-loader-2 h-6 w-6 animate-spin text-muted-foreground" />
        </div>

        <!-- Empty State -->
        <div v-else-if="showEmptyState" class="flex flex-col items-center justify-center py-12 text-center">
          <div class="i-lucide-search-x mb-4 h-12 w-12 text-muted-foreground" />
          <p class="text-lg font-medium">
            {{ t('search.no_results') }}
          </p>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ t('search.try_different_terms') }}
          </p>
        </div>

        <!-- Results Grid -->
        <div v-else-if="hasResults" class="max-h-[600px] overflow-y-auto">
          <!-- Collections Section -->
          <!--          <div v-if="searchCollectionsResults.length > 0" class="border-b p-4"> -->
          <!--            <h3 class="mb-3 text-sm text-muted-foreground font-semibold tracking-wider uppercase"> -->
          <!--              {{ t('search.collections') }} -->
          <!--            </h3> -->
          <!--            <div class="space-y-2"> -->
          <!--              <NuxtLinkLocale -->
          <!--                v-for="collection in searchCollectionsResults" -->
          <!--                :key="collection.id" -->
          <!--                :to="`/collections/${collection.id}/${collection.slug}`" -->
          <!--                class="block rounded p-2 transition-colors hover:bg-slate-100" -->
          <!--                @click="handleClose" -->
          <!--              > -->
          <!--                <div class="flex flex-col gap-1"> -->
          <!--                  <span class="font-medium">{{ collection.name }}</span> -->
          <!--                  <span v-if="formatCollectionPath(collection)" class="text-xs text-muted-foreground"> -->
          <!--                    {{ formatCollectionPath(collection) }} -->
          <!--                  </span> -->
          <!--                </div> -->
          <!--              </NuxtLinkLocale> -->
          <!--            </div> -->
          <!--          </div> -->

          <!-- Products Column -->
          <div v-if="searchResults.length > 0" class="p-4">
            <h3 class="mb-3 text-sm text-muted-foreground font-semibold tracking-wider uppercase">
              {{ t('search.products') }}
            </h3>
            <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
              <ProductCard
                v-for="product in searchResults"
                :key="product.id"
                :item="product"
                @click="handleProductClick"
              />
            </div>
            <!-- Show All Button -->
            <div class="mt-4 text-center">
              <UiButton as-child variant="link">
                <NuxtLinkLocale
                  :to="{ path: '/search', query: { q: searchTerm } }"
                  @click="handleClose"
                >
                  {{ t('general.show_all') }}
                  <Icon name="lucide:arrow-right" class="ml-2 h-4 w-4" />
                </NuxtLinkLocale>
              </UiButton>
            </div>
          </div>
        </div>
      </UiPopoverContent>
    </UiPopover>

    <!-- Mobile/Fallback Search Input (without Popover) -->
    <div v-else-if="!hideInput" class="relative">
      <label for="searchbar" class="sr-only">
        {{ t("general.search.label") }}
      </label>
      <UiInput
        ref="inputRef"
        v-model="searchTerm"
        :placeholder="t('general.search_placeholder')"
        class="bg-gray-100 pl10"
        @focus="handleOpen"
        @keyup.enter="handleSearchSubmit"
      />
      <span
        class="absolute start-0 inset-y-0 flex items-center justify-center px2"
      >
        <Icon name="lucide:search" />
      </span>
    </div>

    <!-- Mobile Full Screen Overlay -->
    <Teleport v-if="(variant === 'overlay' || isMobile) && isOpen" to="body">
      <div class="fixed inset-0 z-50 bg-background">
        <!-- Mobile Header -->
        <div class="flex items-center gap-2 border-b px-4 py-3">
          <button
            class="p-2"
            @click="handleClose"
          >
            <Icon name="lucide:arrow-left" class="h-5 w-5" />
          </button>
          <UiInput
            v-model="searchTerm"
            :placeholder="t('general.search_placeholder')"
            class="flex-1 border-0 px-0 focus-visible:ring-0"
            autofocus
            @keyup.enter="handleSearchSubmit"
          />
        </div>

        <!-- Mobile Results -->
        <div class="h-[calc(100vh-60px)] overflow-y-auto">
          <!-- Loading State -->
          <div v-if="isLoading" class="flex items-center justify-center py-12">
            <div class="i-lucide-loader-2 h-6 w-6 animate-spin text-muted-foreground" />
          </div>

          <!-- Empty State -->
          <div v-else-if="showEmptyState" class="flex flex-col items-center justify-center py-12 text-center">
            <div class="i-lucide-search-x mb-4 h-12 w-12 text-muted-foreground" />
            <p class="text-lg font-medium">
              {{ t('search.no_results') }}
            </p>
            <p class="mt-1 text-sm text-muted-foreground">
              {{ t('search.try_different_terms') }}
            </p>
          </div>

          <!-- Results -->
          <div v-else-if="hasResults" class="px-4 py-4">
            <!-- Collections Section -->
            <!--            <div v-if="searchCollectionsResults.length > 0" class="mb-6"> -->
            <!--              <h3 class="mb-3 text-sm text-muted-foreground font-semibold tracking-wider uppercase"> -->
            <!--                {{ t('search.collections') }} -->
            <!--              </h3> -->
            <!--              <div class="space-y-2"> -->
            <!--                <NuxtLinkLocale -->
            <!--                  v-for="collection in searchCollectionsResults" -->
            <!--                  :key="collection.id" -->
            <!--                  :to="`/collections/${collection.id}/${collection.slug}`" -->
            <!--                  class="block rounded bg-slate-50 p-3 active:bg-slate-100" -->
            <!--                  @click="handleClose" -->
            <!--                > -->
            <!--                  <div class="flex flex-col gap-1"> -->
            <!--                    <span class="font-medium">{{ collection.name }}</span> -->
            <!--                    <span v-if="formatCollectionPath(collection)" class="text-xs text-muted-foreground"> -->
            <!--                      {{ formatCollectionPath(collection) }} -->
            <!--                    </span> -->
            <!--                  </div> -->
            <!--                </NuxtLinkLocale> -->
            <!--              </div> -->
            <!--            </div> -->

            <!-- Products Section -->
            <div v-if="searchResults.length > 0" class="mb-6">
              <h3 class="mb-3 text-sm text-muted-foreground font-semibold tracking-wider uppercase">
                {{ t('search.products') }}
              </h3>
              <div class="grid grid-cols-2 gap-3">
                <ProductCard
                  v-for="product in searchResults"
                  :key="product.id"
                  :item="product"
                  @click="handleProductClick"
                />
              </div>
              <!-- Show All Button -->
              <div class="mt-4 text-center">
                <UiButton as-child variant="link">
                  <NuxtLinkLocale
                    :to="{ path: '/search', query: { q: searchTerm } }"
                    @click="handleClose"
                  >
                    {{ t('general.show_all') }}
                    <Icon name="lucide:arrow-right" class="ml-2 h-4 w-4" />
                  </NuxtLinkLocale>
                </UiButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
